import type React from 'react';
import type { Client } from '@zg-rentals/http-client';
import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import { RentalManagerUserInfoV2WebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-beta-client';
import { hasRequiredRoles, isNotLoggedIn } from './utils';
import { loginUrl } from './utils';
import { makeAuthProvider } from '@zg-rentals/auth-react';

export type User = RentalManagerAuthCheckUserInfoWebApiDto;

export const Auth = makeAuthProvider({
  fetchUser: ({
    client,

    allowedSessionLengthSeconds,
    extendSession,
    includeCanClaimFreeListings,
    includeCustomers,
    includeProfileImageUrl,
    pageWithSensitiveInformation,
  }: {
    client?: Client;

    // Fetch user options
    allowedSessionLengthSeconds?: number;
    extendSession?: boolean;
    includeCanClaimFreeListings?: boolean;
    includeCustomers?: boolean;
    includeProfileImageUrl?: boolean;
    pageWithSensitiveInformation?: boolean;

    // Auth0 MFA required experiment flag
    isAuth0mfaRequiredExp?: boolean;

    // Authorize user options
    allRequiredRoles?: Array<string>;
    oneOfRequiredRoles?: Array<string>;
    requireStrongAuthentication?: boolean;
  }) =>
    // TODO: use `CometUserAuthenticationWebHandler_getUser` instead of `RentalManagerUserInfoV2WebApiHandler_get`
    RentalManagerUserInfoV2WebApiHandler_get(
      {
        allowedSessionLengthSeconds,
        extendSession,
        includeCanClaimFreeListings,
        includeCustomers,
        includeProfileImageUrl,
        pageWithSensitiveInformation,
      },
      client,
    ),

  authorizeUser: (user, { allRequiredRoles, oneOfRequiredRoles, requireStrongAuthentication, isAuth0mfaRequiredExp }) =>
    !isNotLoggedIn(user, requireStrongAuthentication, isAuth0mfaRequiredExp) &&
    hasRequiredRoles({
      allRequiredRoles,
      oneOfRequiredRoles,
      userRoles: user.roles,
    }),
});

export const { useAuth } = Auth;

export function RedirectBoundary({
  loading,
  host,
  ...props
}: { host?: string } & React.ComponentProps<typeof Auth.Boundary>) {
  return (
    <Auth.Boundary
      {...props}
      loading={loading}
      redirectHref={(_, params) =>
        loginUrl({
          host,
          redirectUrl: `${location.pathname}?${params}`,
          crossEnv: process.env.NODE_ENV === 'development',
          protocol: 'https',
          port: process.env.NODE_ENV === 'development' ? Number(location.port) : undefined,
        })
      }
    />
  );
}
